import dayjs from 'dayjs';
import { getTimezoneList } from 'src/api/analytics';

// Single source of truth for "what time is it for this client".
//
// `/timezone?status=1` returns one row per timezone with an offset string in
// `Time` ("-04:00", "+05:30"), and one row carries `IndianTime` -- the server
// clock. Offsets are applied to that server clock, never to the browser clock:
// the browser can be in any timezone and is not part of the calculation.
//
// IST rows are the exception. Their "+05:30" is a UTC offset, so adding it to a
// base that is already IST would double-count. This is the only place that
// exception lives -- change `effectiveOffsetMinutes` if the backend ever
// normalises the column.

export const IST_OFFSET_MINUTES = 5 * 60 + 30;

const TIMEZONE_CACHE_TTL_MS = 5 * 60 * 1000;

let cache = null; // { fetchedAt, indianTime, list }
let inFlight = null;

export const parseOffsetMinutes = (timeOffset) => {
  const match = typeof timeOffset === 'string' ? timeOffset.match(/([+-])(\d{1,2}):(\d{2})/) : null;
  if (!match) return null;
  const [, sign, hours, minutes] = match;
  return (parseInt(hours, 10) * 60 + parseInt(minutes, 10)) * (sign === '+' ? 1 : -1);
};

const effectiveOffsetMinutes = (offsetInMinutes) =>
  offsetInMinutes === IST_OFFSET_MINUTES ? 0 : offsetInMinutes;

// "2025-08-04 16:21:50" -> a naive wall-clock dayjs. Safari refuses the space form.
const parseServerTime = (value) => dayjs(String(value).replace(' ', 'T'));

const fetchTimezoneList = () =>
  new Promise((resolve, reject) => {
    getTimezoneList((response) => {
      if (response.status === 200) {
        resolve(response.data);
      } else {
        reject(new Error('Failed to fetch timezone list'));
      }
    }).catch(reject);
  });

const loadTimezoneData = () => {
  if (cache && Date.now() - cache.fetchedAt < TIMEZONE_CACHE_TTL_MS) {
    return Promise.resolve(cache);
  }
  if (!inFlight) {
    inFlight = fetchTimezoneList()
      .then((list) => {
        const rows = Array.isArray(list) ? list : [];
        const indianTimeEntry = rows.find((item) => item.IndianTime);
        if (!indianTimeEntry || !indianTimeEntry.IndianTime) {
          throw new Error('IndianTime not found in timezone response');
        }
        const indianTime = parseServerTime(indianTimeEntry.IndianTime);
        if (!indianTime.isValid()) {
          throw new Error(`Unparseable IndianTime: ${indianTimeEntry.IndianTime}`);
        }
        cache = { fetchedAt: Date.now(), indianTime, list: rows };
        return cache;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

// The cached IndianTime is a snapshot; roll it forward by however long we have
// been holding it so a cache hit still reads as "now".
const serverNow = (data) => data.indianTime.add(Date.now() - data.fetchedAt, 'millisecond');

const offsetForId = (data, timezoneId) => {
  const row = data.list.find((item) => item.Id === parseInt(timezoneId, 10));
  return row ? parseOffsetMinutes(row.Time) : null;
};

/**
 * Current time in the client's timezone.
 * Rejects if the timezone list is unreachable -- callers decide the fallback.
 * Falls back to server time (IST) when the id itself has no usable row.
 */
export const getClientNow = async (timezoneId) => {
  const id = timezoneId != null ? timezoneId : localStorage.getItem('timezone_id');
  const data = await loadTimezoneData();
  const base = serverNow(data);

  const offset = offsetForId(data, id);
  if (offset === null) {
    console.error('Timezone not found for ID:', id);
    return base;
  }
  return base.add(effectiveOffsetMinutes(offset), 'minute');
};

/**
 * Current time for a merged client spanning several timezones, using the
 * furthest-ahead timezone so nothing is cut off the end of the window.
 */
export const getClientNowForTimezoneIds = async (timezoneIds) => {
  const ids = Array.isArray(timezoneIds) ? timezoneIds : [];
  if (ids.length === 0) return getClientNow();

  const data = await loadTimezoneData();
  const base = serverNow(data);

  const offsets = ids.map((id) => offsetForId(data, id)).filter((offset) => offset !== null);
  if (offsets.length === 0) {
    console.error('No timezones found for IDs:', ids);
    return base;
  }

  const highestOffset = offsets.reduce((max, offset) => (offset > max ? offset : max), -Infinity);
  return base.add(effectiveOffsetMinutes(highestOffset), 'minute');
};

/** The raw timezone rows, for callers that render the list itself. */
export const getTimezoneData = async () => (await loadTimezoneData()).list;

/** Timezone ids of a merged client, deduplicated. */
export const getMergeTimezoneIds = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('merge_timezone_ids') || '[]');
    return [...new Set((Array.isArray(stored) ? stored : []).map((user) => user.TimezoneId))];
  } catch (error) {
    console.error('Failed to parse merge_timezone_ids:', error);
    return [];
  }
};

/**
 * Current time for the logged-in client, picking the merged-client rule
 * automatically. `clientType` is the localStorage `client_type` value.
 */
export const getCurrentClientNow = (clientType, mergeTimezoneIds) => {
  const ids = mergeTimezoneIds != null ? mergeTimezoneIds : getMergeTimezoneIds();
  if (clientType === 'US' && ids.length > 0) {
    return getClientNowForTimezoneIds(ids);
  }
  return getClientNow();
};

/** The wire format every audit/dashboard endpoint expects. */
export const formatApiDateTime = (value) => dayjs(value).format('YYYY-MM-DD HH:mm:ss');
